import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, TextField, Typography, styled } from "@mui/material";
import { useDateFormatting } from "shared-ui/contexts/Locale/useDateFormatting";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import { ActiveTransferFragment, ActiveTransfersTeamsOptionFragment, useActiveTransfersTeamsOptionsQuery } from "apps/web/src/types";
import { useUser } from "apps/web/src/utils/tokens";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Label = styled(Typography)`
  font-weight: 600;
  display: inline;
`;

const TransferDetailsWrapper = styled(Box)(
  ({ theme }) => `
  background-color: ${theme.palette.grey[200]};
  padding: ${theme.spacing(2)};
  margin: ${theme.spacing(1, 0)};
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`
);

interface TransferModalProps {
  transfer: ActiveTransferFragment;
  onClose: () => void;
  open: boolean;
  onReclaim: () => void;
  onRecieve: (teamId: string) => void;
}

export const TransferModal = ({ transfer, onClose, open, onReclaim, onRecieve }: TransferModalProps) => {
  const { t } = useTranslation();
  const user = useUser();
  const mode = user?.orgMembership?.org.id === transfer.releasedByOrg.id ? "reclaim" : "claim";
  const [selectedTeam, setSelectedTeam] = useState<ActiveTransfersTeamsOptionFragment | null>(null);
  const { formatDateWithLocale } = useDateFormatting();

  const { data } = useActiveTransfersTeamsOptionsQuery();

  const birthDateString = transfer.athlete?.birthDate;
  const birthDate = birthDateString ? formatDateWithLocale(new Date(birthDateString)) : "-";

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={onClose}>
      <DialogTitle title={t("transfers.modal.title")} onClose={onClose} />
      <DialogContent sx={{ paddingBottom: theme => theme.spacing(5) }}>
        <Box>
          <Typography variant="h4">{t("transfers.modal.subtitle")}</Typography>
          <TransferDetailsWrapper>
            <Typography>
              <Label>Name:</Label> {transfer.athlete?.lastName}, {transfer.athlete?.firstName}
            </Typography>
            <Typography>
              <Label>Birthdate:</Label> {birthDate}
            </Typography>
            <Typography>
              <Label>Releasing Org:</Label> {transfer.releasedByOrg?.name}
            </Typography>
            {transfer.note && (
              <Typography>
                <Label>Notes:</Label> {transfer.note}
              </Typography>
            )}
          </TransferDetailsWrapper>
        </Box>
        {mode === "reclaim" && (
          <div>
            <Typography variant="h4">{t("transfers.modal.mode.reclaim.title")}</Typography>
            <Typography variant="body2Medium">{t("transfers.modal.mode.reclaim.description")}</Typography>
          </div>
        )}
        {mode === "claim" && (
          <div>
            <Typography variant="h4">{t("transfers.modal.mode.claim.title")}</Typography>
            <Typography variant="body2Medium">{t("transfers.modal.mode.claim.description")}</Typography>

            <Box sx={{ pt: 2, maxWidth: "300px" }}>
              <Autocomplete
                filterSelectedOptions
                options={data?.teams ?? []}
                getOptionLabel={option => option.name}
                renderInput={params => <TextField {...params} label={"Team"} />}
                isOptionEqualToValue={(option: ActiveTransfersTeamsOptionFragment, value: ActiveTransfersTeamsOptionFragment) =>
                  option.id === value.id
                }
                value={selectedTeam}
                onChange={(_, value) => setSelectedTeam(value)}
              />
            </Box>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        {mode === "reclaim" && <Button onClick={onReclaim}>Reclaim</Button>}
        {mode === "claim" && (
          <Button disabled={!selectedTeam} onClick={() => onRecieve(selectedTeam?.id ?? "")}>
            Add to Org
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
